import { z } from 'zod';

import {
  RawSigningPrivateKey,
  RawEncryptionPrivateKey,
  loadRawSigningPrivateKey,
  loadRawEncryptionPrivateKey,
} from 'vault-crypto';

import { brandedUint8Array } from '../../brands';

import { PersistableAuthenticator } from './PersistableAuthenticator';

/**
 * WARNING! The types defined in this file are used by ClientCoreParameters and can only be changed
 * in backwards compatible ways! See ClientCoreParameters.ts for details.
 */

interface PersistableUnlockedAuthenticator {
  readonly authenticator: PersistableAuthenticator;
  readonly storableSigPrivKey: RawSigningPrivateKey;
  readonly storableProfileSeedEncPrivKey: RawEncryptionPrivateKey;
}

const UnlockedAuthenticatorV3 = z.object({
  authenticator: PersistableAuthenticator,
  storableSigPrivKey: brandedUint8Array(loadRawSigningPrivateKey),
  storableProfileSeedEncPrivKey: brandedUint8Array(loadRawEncryptionPrivateKey),
});

const UnlockedAuthenticatorV2 = z
  .object({
    authenticator: PersistableAuthenticator,
    storableSigPrivKey: brandedUint8Array(loadRawSigningPrivateKey),
    storableVaultKeyEncPrivKey: brandedUint8Array(loadRawEncryptionPrivateKey),
  })
  .transform(({ storableVaultKeyEncPrivKey, ...rest }) => ({
    storableProfileSeedEncPrivKey: storableVaultKeyEncPrivKey,
    ...rest,
  }));

const UnlockedAuthenticatorV1 = z
  .object({
    authenticator: PersistableAuthenticator,
    sigPrivKey: brandedUint8Array(loadRawSigningPrivateKey),
    encPrivKey: brandedUint8Array(loadRawEncryptionPrivateKey),
  })
  .transform(({ sigPrivKey, encPrivKey, ...rest }) => ({
    storableSigPrivKey: sigPrivKey,
    storableProfileSeedEncPrivKey: encPrivKey,
    ...rest,
  }));

const PersistableUnlockedAuthenticator = z.union([
  UnlockedAuthenticatorV1,
  UnlockedAuthenticatorV2,
  UnlockedAuthenticatorV3,
]);

export { PersistableUnlockedAuthenticator };
